// T-165 (v1.8.46, Session 40, 02.10.2026): Rechnung stornieren und Nachmeldung abrechnen in der Verwaltung (Rechnungen).
// Reine Hilfsfunktionen für die Oberfläche; die verbindliche Prüfung und Berechnung macht der Server
// (Business.Invoicing.Rechnungsstorno und NachmeldungAbrechnung). Getestet in rechnungskorrektur.test.ts.

const STATUS_NAMEN = ['Draft', 'Created', 'Sent', 'Viewed', 'Paid', 'PartiallyPaid', 'Overdue', 'Cancelled', 'Disputed'];

/** Rechnungsstatus als Name, auch wenn die API die Zahl des Enums liefert. */
export function statusName(status: string | number | null | undefined): string {
  if (typeof status === 'number') return STATUS_NAMEN[status] ?? String(status);
  return (status ?? '').toString();
}

export interface StornoKandidat {
  status: string | number;
  typeDisplayName?: string;
  amountPaid?: number;
}

/** True, wenn die Verwaltung die Rechnung stornieren kann (ohne Zahlung, nicht Angebot/Storno/Gutschrift, nicht storniert). */
export function istStornierbar(r: StornoKandidat | null | undefined): boolean {
  if (!r) return false;
  const s = statusName(r.status);
  if (['Paid', 'PartiallyPaid', 'Cancelled'].includes(s)) return false;
  if (['Angebot', 'Storno', 'Gutschrift'].includes(r.typeDisplayName ?? '')) return false;
  return (r.amountPaid ?? 0) <= 0;
}

export const MIN_STORNO_GRUND = 3;

export interface NachmeldungPosition {
  schluessel: string;
  beschreibung: string;
  menge: number;
  einzelpreis: number;
  gesamt: number;
  steuersatz: number;
  steuerSplit: boolean;
  bereitsNachberechnet: number;
  maxMenge: number;
  sperrgrund?: string | null;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Betrag einer gewählten Menge wie auf dem Server (Gesamt minus anteiliger Rest, auf Cent gerundet; Vorschau, verbindlich ist der Server). */
export function betragFuerMenge(p: NachmeldungPosition, menge: number): number {
  if (!menge || menge <= 0 || p.menge <= 0) return 0;
  const rest = p.menge - menge;
  return r2(p.gesamt - (rest > 0 ? r2((p.gesamt * rest) / p.menge) : 0));
}

/** Summe der Auswahl. */
export function summeAuswahl(positionen: NachmeldungPosition[], mengen: Record<string, number>): number {
  return r2(positionen.reduce((s, p) => s + betragFuerMenge(p, mengen[p.schluessel] ?? 0), 0));
}

/** Fehlermeldung der Auswahl oder null. */
export function auswahlFehler(positionen: NachmeldungPosition[], mengen: Record<string, number>): string | null {
  for (const p of positionen) {
    const m = mengen[p.schluessel] ?? 0;
    if (m < 0 || !Number.isInteger(m)) return `${p.beschreibung}: bitte eine ganze Zahl ab 0 eintragen.`;
    if (m > p.maxMenge) return `${p.beschreibung}: höchstens ${p.maxMenge} wählbar.`;
  }
  if (!positionen.some(p => (mengen[p.schluessel] ?? 0) > 0)) return 'Bitte bei mindestens einer Position eine Menge angeben.';
  return null;
}

export function euro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
